import styled, { css } from 'styled-components'
import { editorItem } from '@/styles/editorItem'
import { itemFeather } from '@/styles/itemFeather'
import {
  getCombinedBoxShadow,
  getImageBorderOutline,
} from '@/styles/getImageBoxShadow'
import { Flex } from '@/components/priority/Flex/Flex'
import { zLayers } from '@/utils/styling/zLayers'

export const ImageWrapper = styled.div`
  position: absolute;
  top: ${({ top }) => top || 0}px;
  left: ${({ left }) => left || 0}px;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  pointer-events: auto;
  overflow: hidden;
  box-shadow: ${({ edits }) => getCombinedBoxShadow(edits)};
  border-radius: ${({ edits }) =>
    edits?.circle ? '50%' : `${edits?.borderRadius || 0}px`};
  opacity: ${({ edits }) => edits?.opacity};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: ${({ edits }) => getImageBorderOutline(edits)};
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
    box-sizing: border-box;
  }
  ${({ edits, width, height }) => itemFeather(edits, width, height, 'image')}
`

export const ImageContainer = styled.div(
  ({ $isEditorEnvironment, width, height, z }) => css`
    ${editorItem}
    position: ${$isEditorEnvironment ? 'relative' : 'absolute'};
    width: ${width}px;
    height: ${height}px;
    z-index: ${zLayers.getZ(z)};
  `,
)

export const CroppedImage = styled.div.attrs(
  ({ imgSrc, left, top, width, height, $cropRight, $cropBottom }) => ({
    style: {
      backgroundImage: `url("${imgSrc}")`,
      backgroundSize: 'cover',
      left: -left,
      top: -top,
      width: width,
      height: height,
      /* Clip the GPU layer to the parent's visible bounds to prevent sub-pixel
         bleed at fractional zoom levels. Applied here (not on the parent) so the
         parent's box-shadow remains unclipped. */
      clipPath: `inset(${top || 0}px ${$cropRight || 0}px ${$cropBottom || 0}px ${left || 0}px)`,
    },
  }),
)(
  () => css`
    position: absolute;
    /* use "center top" to keep portrait heads visible (vs. "center" only, which often crops them) */
    background-position: center top;
    background-repeat: no-repeat;
    border-radius: inherit;
    pointer-events: none;
    /* Force GPU compositing layer from the start to prevent fuzziness when z-index changes */
    transform: translateZ(0);
    backface-visibility: hidden;
    will-change: transform;
  `,
)

/**
 * Small "Edited with AI" pill shown on an editor image whose pixels came
 * from an accepted AI alteration. Editor-only: it never renders in preview
 * or PDF environments, and it ignores pointer events so item selection and
 * dragging behave exactly as before.
 */
export const AiEditedTag = styled.span`
  position: absolute;
  left: 6px;
  bottom: 6px;
  z-index: ${({ $zIndex }) => $zIndex};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: #ffffff;
  background-color: rgba(0, 0, 0, 0.7);
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
`

export const ProcessingOverlay = styled(Flex)`
  pointer-events: none;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #e0e0e0;
    border-top-color: #1a6cab;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`
